/**
 * Minimal path router — enough for real URLs without pulling in a routing library.
 *
 * Routes: `/general-overview` · `/insights` · `/suppliers` · `/modules` · `/tools`
 * · `/login` sign-in · `/users` account administration · `/invite?token=…` accept
 * an invitation · `/reset?token=…` set a new password. The old `/` and child
 * route aliases remain for existing bookmarks.
 *
 * The Vite dev server (and any SPA host) already falls back to index.html for
 * unknown paths, so deep links work on refresh.
 */
import { useEffect, useState } from 'react'

const ROUTE_EVENT = 'storeos:navigate'

export interface Route {
  path: string
  query: URLSearchParams
}

export function currentRoute(): Route {
  if (typeof window === 'undefined') {
    return { path: '/', query: new URLSearchParams() }
  }
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  return { path, query: new URLSearchParams(window.location.search) }
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return
  const url = new URL(to, window.location.origin)
  const target = `${url.pathname}${url.search}`
  if (`${window.location.pathname}${window.location.search}` === target) return
  if (options.replace) {
    window.history.replaceState({}, '', target)
  } else {
    window.history.pushState({}, '', target)
  }
  window.dispatchEvent(new Event(ROUTE_EVENT))
}

/** Re-renders the component when the browser navigates (back/forward included). */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute)

  useEffect(() => {
    const sync = () => setRoute(currentRoute())
    window.addEventListener('popstate', sync)
    window.addEventListener(ROUTE_EVENT, sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener(ROUTE_EVENT, sync)
    }
  }, [])

  return route
}

/** Paths the app knows about; anything else redirects to the dashboard. */
export const KNOWN_PATHS = [
  '/',
  '/general-overview',
  '/insights',
  '/suppliers',
  '/modules',
  '/tools',
  '/login',
  '/setup-2fa',
  '/users',
  '/account',
  '/profile',
  '/insights/mobietrain-insights',
  '/insights/four-wall-analysis',
  '/insights/grid-viewer',
  '/insights/sales-analysis',
  '/insights/product-overview',
  // Legacy aliases kept so existing bookmarks still open the right page.
  '/insights/mobietrain',
  '/insights/four-wall',
  '/insights/grid',
  '/insights/sales',
  '/insights/products',
  '/invite',
  '/reset-password',
  '/verify-email',
]

/** Paths that are reachable without a session. */
export const PUBLIC_PATHS = ['/login', '/invite', '/reset-password', '/verify-email']

export function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.includes(path)
}

export function isKnownPath(path: string): boolean {
  return KNOWN_PATHS.includes(path)
}
